import React, { useState } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import {
    Section,
    Header,
    Title,
    Grid,
    EmptyState,
    AddButton,
    IconButton,
    LoadingState,
    LoadingSpinner
} from '../../styles/BookmarksSection.styles';
import { BookmarkIcon } from '../icons/BookmarkIcon';
import PlusIcon from '../icons/PlusIcon';
import { ViewIcon } from '../icons/ViewIcon';
import BookmarkCard from './BookmarkCard';
import AddBookmarkModal from './AddBookmarkModal';
import { useBookmarks } from '../../hooks/useBookmarks';

const BookmarksSection = ({ userId }) => {
    const {
        bookmarks,
        loading,
        error,
        addBookmark,
        removeBookmark,
        updateBookmark,
        updateOrder
    } = useBookmarks(userId);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCompactView, setIsCompactView] = useLocalStorage('bookmarksCompactView', true);

    const handleToggleCompactView = () => {
        setIsCompactView(!isCompactView);
    };

    const handleDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination || (destination.index === source.index && destination.droppableId === source.droppableId)) {
            return;
        }

        const newBookmarks = Array.from(bookmarks);
        const [removed] = newBookmarks.splice(source.index, 1);
        newBookmarks.splice(destination.index, 0, removed);

        updateOrder(newBookmarks.map(bookmark => bookmark.id));
    };

    const handleAddBookmark = async (url) => {
        try {
            await addBookmark(url);
        } catch (error) {
            console.error('Error adding bookmark:', error);
        }
    };

    const handleRemoveBookmark = async (bookmarkId) => {
        try {
            await removeBookmark(bookmarkId);
        } catch (error) {
            console.error('Error removing bookmark:', error);
        }
    };

    const handleUpdateBookmark = async (bookmarkId, updates) => {
        try {
            await updateBookmark(bookmarkId, updates);
        } catch (error) {
            console.error('Error updating bookmark:', error);
        }
    };

    if (loading) {
        return (
            <Section>
                <LoadingState>
                    <LoadingSpinner />
                </LoadingState>
            </Section>
        );
    }

    if (error) {
        return (
            <Section>
                <EmptyState>
                    <p>Error loading bookmarks. Please try again later.</p>
                </EmptyState>
            </Section>
        );
    }

    return (
        <Section>
            <Header>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <IconButton onClick={() => setIsModalOpen(true)}>
                        <PlusIcon />
                    </IconButton>
                    <IconButton onClick={handleToggleCompactView} title={isCompactView ? "Grid View" : "Compact View"}>
                        <ViewIcon />
                    </IconButton>
                </div>
            </Header>
            <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="bookmarks" direction="horizontal">
                    {(provided, snapshot) => (
                        <Grid
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={snapshot.isDraggingOver ? 'is-dragging-over' : ''}
                            isCompactView={isCompactView}
                        >
                            {bookmarks.length === 0 ? (
                                <EmptyState>
                                    <BookmarkIcon />
                                    <p>No bookmarks yet. Add your first bookmark to get started!</p>
                                    <AddButton onClick={() => setIsModalOpen(true)}>
                                        <PlusIcon />
                                        Add Bookmark
                                    </AddButton>
                                </EmptyState>
                            ) : (
                                bookmarks.map((bookmark, index) => (
                                    <BookmarkCard
                                        key={bookmark.id}
                                        bookmark={bookmark}
                                        index={index}
                                        onRemove={handleRemoveBookmark}
                                        onUpdate={(updates) => handleUpdateBookmark(bookmark.id, updates)}
                                        isCompactView={isCompactView}
                                    />
                                ))
                            )}
                            {provided.placeholder}
                        </Grid>
                    )}
                </Droppable>
            </DragDropContext>

            <AddBookmarkModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                userId={userId}
                onAdd={handleAddBookmark}
            />
        </Section>
    );
};

export default BookmarksSection;